import React from 'react';
import { formatDay } from '../../data/dailyLanguage';
import { wrapReading } from '../BirthChart/ReadingText';

const TAG = /^##\s*\[(\w+)\]\s*(.*)$/;
const TRANSIT = /\s*\[T(\d+)\]\s*$/;

const AREAS = [
  { key: 'relationships', icon: '♡' },
  { key: 'career', icon: '✦' },
  { key: 'growth', icon: '✿' },
];

function clean(text) {
  return text.replace(/\*\*|__/g, '').replace(/^[-*]\s+/, '').trim();
}

export function parseReading(text) {
  if (!text || !/^##\s*\[\w+\]/m.test(text)) return null;
  const sections = [];
  let section = null;
  let item = null;
  text.split('\n').forEach((raw) => {
    const line = raw.trim();
    const tag = line.match(TAG);
    if (tag) {
      section = { tag: tag[1].toLowerCase(), arg: tag[2].trim(), items: [] };
      sections.push(section);
      item = null;
      return;
    }
    if (!section || !line) return;
    if (line.startsWith('#')) {
      let headline = clean(line.replace(/^#+\s*/, ''));
      let transit = null;
      const found = headline.match(TRANSIT);
      if (found) {
        transit = Number(found[1]) - 1;
        headline = headline.replace(TRANSIT, '');
      }
      item = { headline, transit, text: [] };
      section.items.push(item);
      return;
    }
    if (!item) {
      item = { headline: '', transit: null, text: [] };
      section.items.push(item);
    }
    item.text.push(clean(line.replace(TRANSIT, '')));
  });
  return sections;
}

function Paragraphs({ lines }) {
  return lines.map((line, index) => <p key={index}>{line}</p>);
}

const DailyReading = ({ text, view, copy, language, transits, onDetails }) => {
  const sections = parseReading(text);
  if (!sections) return text ? wrapReading(text, language) : null;
  const find = (tag) => sections.find((section) => section.tag === tag);
  const glance = find(view === 'week' ? 'week' : 'glance');
  const themes = find('themes');
  const reflection = find('reflection');
  const days = sections.filter((section) => section.tag === 'day');
  const glanceLabel = {
    today: copy.glanceToday || 'Your day at a glance',
    tomorrow: copy.glanceTomorrow || 'Tomorrow at a glance',
    week: copy.glanceWeek || 'Your week at a glance',
  }[view];
  const reflectionLabel = {
    today: copy.reflectionToday || 'A reflection for today',
    tomorrow: copy.reflectionTomorrow || 'A reflection for tomorrow',
    week: copy.reflectionWeek || 'To reflect on this week',
  }[view];
  const detailsButton = (index) => (
    index != null && transits?.[index] ? (
      <button type="button" className="daily-why" onClick={() => onDetails(transits[index])}>
        {copy.techDetails || 'View technical details'} →
      </button>
    ) : null
  );

  return (
    <div className="daily-reading">
      {glance?.items[0] && (
        <section className="daily-glance">
          <span className="daily-label">{glanceLabel}</span>
          {glance.items[0].headline && <h2>{glance.items[0].headline}</h2>}
          <Paragraphs lines={glance.items.flatMap((item) => item.text)} />
        </section>
      )}

      {themes?.items.length > 0 && (
        <section className="daily-block">
          <h2 className="daily-block-title">{copy.themesTitle || 'Main themes'}</h2>
          <div className="daily-theme-grid">
            {themes.items.map((item, index) => (
              <article className="daily-theme-card" key={index}>
                {item.headline && <h3>{item.headline}</h3>}
                <Paragraphs lines={item.text} />
                {detailsButton(item.transit)}
              </article>
            ))}
          </div>
        </section>
      )}

      {view !== 'week' && AREAS.some(({ key }) => find(key)) && (
        <section className="daily-areas">
          {AREAS.map(({ key, icon }) => {
            const area = find(key);
            if (!area) return null;
            const first = area.items[0] || { headline: '', text: [] };
            return (
              <article className={`daily-area daily-area-${key}`} key={key}>
                <span className="daily-area-label"><span aria-hidden="true">{icon}</span> {copy.themes?.[key]}</span>
                {first.headline && <h3>{first.headline}</h3>}
                <Paragraphs lines={area.items.flatMap((item) => item.text)} />
              </article>
            );
          })}
        </section>
      )}

      {days.length > 0 && (
        <section className="daily-block">
          <h2 className="daily-block-title">{copy.weekTitle || 'Day by day'}</h2>
          <ol className="daily-days">
            {days.map((day, index) => (
              <li key={`${day.arg}-${index}`}>
                <span className="daily-day-date">{/^\d{4}-\d{2}-\d{2}$/.test(day.arg) ? formatDay(day.arg, language) : day.arg}</span>
                <div>
                  {day.items[0]?.headline && <h3>{day.items[0].headline}</h3>}
                  <Paragraphs lines={day.items.flatMap((item) => item.text)} />
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {reflection?.items.length > 0 && (
        <section className="daily-reflection">
          <span className="daily-label">{reflectionLabel}</span>
          {reflection.items.map((item, index) => (
            <blockquote key={index}>
              {item.headline && <p className="daily-reflection-lead">{item.headline}</p>}
              <Paragraphs lines={item.text} />
            </blockquote>
          ))}
        </section>
      )}
    </div>
  );
};

export default DailyReading;
